import React, { useState, useEffect } from "react";
import "./map.css";

const UserEventVisualSelector = ({
  setSidebarData,
  GroupsEvents,
  event,
  selectedQuantity,
  onTicketSelect,
  selectedTicketId,
  hoveredTicketId
}) => {
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Seatics-style color palette - matching the actual Seatics colors
  const seaticsColors = [
    "#d86f6f", "#e2a6a6", "#f4ad58", "#ffd797",
    "#bc91c1", "#e5cae2", "#66b6dd", "#a3ddf8",
    "#abcb59", "#d2e08b", "#bfa082", "#d9d7ba",
    "#589caf", "#bcd3e7", "#fff871", "#fff7a9",
    "#9bc9a2", "#c7ddc8"
  ];

  useEffect(() => {
    // Update sidebar data when component mounts or GroupsEvents changes
    if (GroupsEvents && GroupsEvents.length > 0) {
      setSidebarData(GroupsEvents);
    }
  }, [GroupsEvents, setSidebarData]);

  useEffect(() => {
    // Update selected ticket when selectedTicketId changes
    if (selectedTicketId && GroupsEvents) {
      const ticket = GroupsEvents.find(t => t.id === selectedTicketId || t.tgID === selectedTicketId);
      setSelectedTicket(ticket || null);
    }
  }, [selectedTicketId, GroupsEvents]);

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    if (onTicketSelect) {
      onTicketSelect(ticket);
    }
  };

  const getTicketColor = (index) => {
    return seaticsColors[index % seaticsColors.length];
  };

  const getTicketAvailability = (ticket) => {
    const available = ticket.available_count || ticket.tgQty || ticket.available_quantity || 0;
    return available;
  };

  return (
    <div className="w-[100%] h-[100%] bg-white relative user-event-visual-selector">
      <div id="mapContainer" className="user-event-visual-selector__map w-[100%] min-h-[240px] h-full max-h-[400px] sm:max-h-none sm:h-[400px] md:h-[600px] 2xl:h-[100%] relative bg-white overflow-y-auto overflow-x-hidden">
        {/* Loading state similar to Seatics */}
        {(!GroupsEvents || GroupsEvents.length === 0) && (
          <div className="w-[100%] h-[100%] absolute top-0 left-0 z-40 bg-white flex justify-center place-items-center">
            <div className="inline-block h-[60px] w-[60px] animate-spin rounded-full border-4 border-solid border-current border-r-transparent text-black align-[-0.125em] motion-reduce:animate-[spin_.5s_linear_infinite]" role="status">
              <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Loading...</span>
            </div>
          </div>
        )}

        {/* Main Venue Layout - scrollable on mobile */}
        <div className="relative w-full min-h-full bg-white flex items-center justify-center p-2 sm:p-0">
          {/* Central Ticket Selection Area - compact on mobile */}
          <div className="w-full max-w-4xl mx-auto p-3 sm:p-6 md:p-8">
            <div className="text-center mb-3 sm:mb-6 md:mb-8">
              <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-800 mb-1 sm:mb-2">Select Your Ticket Type</h3>
              <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Choose from available ticket categories below</p>
            </div>

            {/* Ticket Types Grid - single column on mobile, tighter spacing */}
            <div className={`grid gap-2 sm:gap-4 ${
              GroupsEvents?.length === 1 ? 'grid-cols-1 max-w-md mx-auto' :
              GroupsEvents?.length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-2xl mx-auto' :
              GroupsEvents?.length === 3 ? 'grid-cols-1 md:grid-cols-3 max-w-4xl mx-auto' :
              'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto'
            }`}>
              {GroupsEvents && GroupsEvents.map((ticket, index) => {
                const isSelected = selectedTicket?.id === ticket.id;
                const isHovered = hoveredTicketId === ticket.id || hoveredTicketId === ticket.tgID;
                const availability = getTicketAvailability(ticket);
                const color = getTicketColor(index);
                const canSelect = selectedQuantity ? availability >= selectedQuantity : true;
                const ticketName = ticket.ticketName || ticket.name || ticket.section || `Ticket ${index + 1}`;

                return (
                  <div
                    key={ticket.id || index}
                    role="button"
                    tabIndex={0}
                    onClick={() => canSelect && handleTicketClick(ticket)}
                    onKeyDown={(e) => {
                      if ((e.key === "Enter" || e.key === " ") && canSelect) {
                        e.preventDefault();
                        handleTicketClick(ticket);
                      }
                    }}
                    className={`
                      relative transition-all duration-300 border-2 rounded-lg p-3 sm:p-4 md:p-6
                      ${isSelected
                        ? 'border-yellow-400 shadow-xl bg-yellow-50 sm:scale-105'
                        : isHovered
                          ? 'border-blue-400 shadow-lg bg-blue-50 sm:scale-102'
                          : canSelect
                            ? 'border-gray-300 hover:border-gray-400 hover:shadow-md'
                            : 'border-gray-200 opacity-60 bg-gray-50'
                      }
                    `}
                    style={{
                      backgroundColor: isSelected ? '#fef3c7' : isHovered ? '#eff6ff' : '#ffffff',
                      boxShadow: isSelected ? `0 0 25px ${color}40` : isHovered ? `0 0 20px #3b82f640` : undefined,
                      minHeight: 'clamp(90px, 20vw, 160px)'
                    }}
                  >
                    {/* Ticket Type Header - compact on mobile */}
                    <div className="text-center mb-2 sm:mb-4">
                      <div
                        className="w-3 h-3 sm:w-4 sm:h-4 rounded-full mx-auto mb-2 sm:mb-3 border-2 border-white shadow-sm inline-block"
                        style={{ backgroundColor: color }}
                      />
                      <h4 className="text-sm sm:text-base md:text-lg font-bold text-gray-800 leading-tight line-clamp-2">
                        {ticketName}
                      </h4>
                    </div>

                    {/* Price & Availability - tighter on mobile */}
                    <div className="text-center space-y-1 sm:space-y-2">
                      <div className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                        ${ticket.retail_price || ticket.price || ticket.tgPrice || 0}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        {availability} available
                      </div>

                      {/* Service fee indicator */}
                      {ticket.service_fee > 0 && (
                        <div className="text-xs text-gray-500">
                          + ${ticket.service_fee} service fee
                        </div>
                      )}

                      {/* Availability status */}
                      {selectedQuantity && (
                        <div className={`inline-block px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-xs font-semibold ${
                          availability >= selectedQuantity
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {availability >= selectedQuantity ? 'Available' : 'Insufficient'}
                        </div>
                      )}
                    </div>

                    {/* Selection indicator - smaller on mobile */}
                    {isSelected && (
                      <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-5 h-5 sm:w-6 sm:h-6 bg-yellow-400 rounded-full border-2 border-white flex items-center justify-center shadow-lg">
                        <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}

                    {/* Sold out overlay */}
                    {selectedQuantity && !canSelect && (
                      <div className="absolute inset-0 bg-red-500 bg-opacity-10 rounded-lg flex items-center justify-center">
                        <div className="bg-red-500 text-white px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-bold">
                          SOLD OUT
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Legend - hidden on small screens to save space */}
        <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 bg-white bg-opacity-95 rounded-lg p-2 sm:p-3 shadow-lg border hidden md:block">
          <div className="text-xs font-bold text-gray-800 mb-1 sm:mb-2">TICKET STATUS</div>
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-yellow-400 rounded border border-white shadow-sm"></div>
              <span className="text-xs text-gray-700">Selected</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-400 rounded border border-white shadow-sm"></div>
              <span className="text-xs text-gray-700">Hovered</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 border-2 border-gray-300 rounded bg-white"></div>
              <span className="text-xs text-gray-700">Available</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded border border-white opacity-60"></div>
              <span className="text-xs text-gray-700">Sold Out</span>
            </div>
          </div>
        </div>

        {/* Event info overlay - compact on mobile */}
        <div className="absolute top-2 right-2 sm:top-4 sm:left-4 sm:right-auto bg-white bg-opacity-95 rounded-lg p-2 sm:p-3 shadow-lg border max-w-[45%] sm:max-w-xs">
          <div className="text-xs sm:text-sm font-bold text-gray-800 truncate">{event?.name}</div>
          <div className="text-[10px] sm:text-xs text-gray-600 mt-0.5 sm:mt-1">
            {GroupsEvents ? `${GroupsEvents.length} type${GroupsEvents.length !== 1 ? 's' : ''}` : '...'}
          </div>
          {selectedQuantity && (
            <div className="text-[10px] sm:text-xs text-red-600 mt-0.5 sm:mt-1 font-semibold">
              Qty: {selectedQuantity}
            </div>
          )}
        </div>

        {/* No tickets overlay */}
        {(!GroupsEvents || GroupsEvents.length === 0) && (
          <div className="absolute top-0 left-0 w-[100%] h-[100%] bg-white z-50 flex justify-center items-center">
            <div className="text-center">
              <div className="text-gray-400 mb-4">
                <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v8a2 2 0 002 2h14a2 2 0 002-2V7a2 2 0 00-2-2H5z" />
                </svg>
              </div>
              <h2 className="text-gray-800 text-xl font-bold mb-2">No Tickets Available</h2>
              <p className="text-gray-600">This event has no ticket types configured</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(UserEventVisualSelector);